import React, { useState, useEffect } from 'react';

// Material
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';

// Components
import Modal from '../modal/index';
import { Spinner } from '@arredo/reactjs-ui';

// Services
import CustomerService from '../../services/customerService';

// Hooks
import useNotify from '../../hooks/notify';
import useCustomer from '../../hooks/customers/useCustomer';
import CustomerProvider from '../../hooks/customers/CustomerProvider';
import useGenericCustomer from '../../hooks/customers/useGenericCustomer';

import CustomersList from './list';
import CustomerCreate from '../../views/Customers/create/createCustomer';
import useCart from '../../hooks/orders/CartProvider';

import { countriesEnum, DocumentType, TaxConditionType } from '../../models/EnumEntity';
import IsDocumentValid from '../../utils/documentCheck';

const CustomerModal = ({ onAccept, onClose, ...rest }) => {
    const { customer, replaceCustomer, loading } = useCustomer();
    const [showEditionMode, setShowEditionMode] = useState(false);
    const { Notify, variants } = useNotify()
    const [isEditable, setIsEditable] = useState(false);
    const [restoreCustomer, setRestoreCustomer] = useState(customer);
    const [hasNotInvoiceAddress, setHasNotInvoiceAddress] = useState(false);
    const [hasNotShippingAddress, setHasNotShippingAddress] = useState(false);
    const { genericCustomer, loading: loadingGeneric } = useGenericCustomer();

    const isFormValid = (customerForm) => {
        if (!customerForm?.firstName || !customerForm?.documentType || !customerForm?.documentNumber ||
            !customerForm?.country || !customerForm?.email || !customerForm?.taxCondition)
            return false;
        if (customerForm?.taxCondition !== "RegisteredManager" && !customerForm?.gender)
            return false;

        return true;
    }

    useEffect(() => {
        if (customer) {
            if (customer.taxCondition === TaxConditionType.RegisteredManager &&
                customer.operationCountry === countriesEnum.AR &&
                customer.taxConfig)
                if (!customer.taxConfig.iibb) {
                    Notify("Los clientes de tipo Responsable inscripto deben completar los datos de Ingresos brutos.", variants.ERROR);
                }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customer.taxConfig?.iibb])

    useEffect(() => {
        if (hasNotInvoiceAddress) {
            Notify("El cliente no posee una dirección de Facturación", variants.ERROR);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasNotInvoiceAddress])

    useEffect(() => {
        if (hasNotShippingAddress) {
            Notify("El cliente no posee una dirección de Entrega", variants.ERROR);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasNotShippingAddress])

    useEffect(() => {
        if (customer.id) {
            setShowEditionMode(true);
            setHasNotInvoiceAddress(false);
            setHasNotShippingAddress(false);
        }
    }, [customer.id])

    const handleClose = () => {
        setIsEditable(false);

        if (!customer.id || !restoreCustomer?.id) {
            setShowEditionMode(false);
            return;
        }

        replaceCustomer(restoreCustomer);
        setShowEditionMode(true);
    }

    const handleSearchCustomer = () => {
        replaceCustomer();
        setRestoreCustomer();
        setIsEditable(false);
        setShowEditionMode(false);
    }

    const handleEditCustomer = () => {
        setRestoreCustomer(customer);
        setIsEditable(true);
    }

    const handleSelectCustomer = (customer) => {
        replaceCustomer(customer);
        setShowEditionMode(true);
        setIsEditable(!isFormValid(customer))
    }

    const handleCreateCustomer = () => {
        setIsEditable(true);
        setShowEditionMode(true);
    }

    const handleGenericCustomer = () => {
        if (genericCustomer)
            handleSelectCustomer(genericCustomer);
        else
            Notify('El cliente genérico no está disponible', variants.ERROR);
    }

    const handleAccept = () => {
        if (!isFormValid(customer)) {
            handleEditCustomer()
            return Notify('Falta completar campos obligatorios', variants.ERROR)
        }
        onAccept(customer)
    }

    const disableAceptar = () => {
        if (!customer) return true;
        if (!customer?.id) return true;
        if (loading) return true;

        if (customer.taxCondition === TaxConditionType.RegisteredManager &&
            customer.operationCountry === countriesEnum.AR &&
            !customer.taxConfig?.iibb)
            return true;

        if (customer?.taxCondition === TaxConditionType.Exempt &&
            customer?.operationCountry === countriesEnum.AR &&
            customer?.documentType !== DocumentType.CUIT)
            return true;

        if (!IsDocumentValid(customer?.documentType, customer?.documentNumber))
            return true;

        if (!customer.addresses)
            return true;

        if (!customer.addresses.some(i => i.isInvoice)) {
            setHasNotInvoiceAddress(true);
            return true;
        }

        if (!customer.addresses.some(i => i.isShipping)) {
            setHasNotShippingAddress(true);
            return true;
        }

        return false;
    }

    return (
        <Modal
            {...rest}
            title='Cliente'
            maxWidth={showEditionMode ? 'lg' : 'sm'}
            onClose={onClose}
            contentProps={{ dividers: true }}
            customButtons={() =>
                showEditionMode
                    ? !isEditable &&
                    <DialogActions>
                        <Button
                            color='primary'
                            onClick={handleSearchCustomer}
                        >
                            Buscar Cliente
                        </Button>
                        <Button
                            disabled={!customer?.id}
                            color='primary'
                            onClick={handleEditCustomer}
                        >
                            Editar
                        </Button>
                        <Button
                            disabled={disableAceptar()}
                            onClick={handleAccept}
                            variant='outlined'
                            color='primary'
                        >
                            Aceptar
                        </Button>
                    </DialogActions>
                    : (
                        <DialogActions>
                            <Button
                                color='primary'
                                onClick={onClose}
                            >
                                Cerrar
                            </Button>
                            <Button
                                disabled={loadingGeneric}
                                onClick={handleGenericCustomer}
                                variant='outlined'
                                color='primary'
                            >
                                Cliente genérico
                            </Button>
                            <Button
                                onClick={handleCreateCustomer}
                                variant='outlined'
                                color='primary'
                            >
                                Crear cliente
                            </Button>
                        </DialogActions>
                    )}
        >
            {
                !showEditionMode &&
                <Grid size={12}>
                    <CustomersList
                        replaceCustomer={replaceCustomer}
                        selectCustomer={handleSelectCustomer}
                        validCustomer={isFormValid}
                    />
                </Grid>
            }
            {
                showEditionMode &&
                <Grid size={12}>
                    <CustomerCreate
                        onAccept={(customer) => onAccept(customer, true)}
                        onCancel={handleClose}
                        disabled={!isEditable}
                    />
                </Grid>
            }
        </Modal>
    );
}

const MainModalCustomer = ({ onClose, ...rest }) => {
    const { Notify, variants } = useNotify();
    const { sale: { customer: customerSale }, update } = useCart();
    const [loading, setLoading] = useState(false);
    const [customer, setCustomer] = useState(null);

    useEffect(() => {
        if (customerSale?.id) {
            setLoading(true);
            CustomerService
                .Get(customerSale?.id)
                .then(setCustomer)
                .catch(() => Notify("Ocurrio un problema al buscar el cliente", variants.ERROR))
                .finally(() => setLoading(false));
        }
    }, [Notify, customerSale?.id, variants.ERROR]);

    const handleSave = async (customer, needUpdate) => {
        if (needUpdate || customer.id !== customerSale?.id)
            update({ customer });

        onClose();
    }

    return (
        <>
            {
                loading &&
                <Grid container justifyContent='center'>
                    <Spinner lockScreen message='Buscando cliente' />
                </Grid>
            }
            {
                !loading &&
                <CustomerProvider customer={customer}>
                    <CustomerModal
                        onAccept={handleSave}
                        onClose={onClose}
                        {...rest}
                    />
                </CustomerProvider>
            }
        </>
    )
}

export default MainModalCustomer;